// Shared chrome for onboarding steps on the right side of the Register page.
// Mirrors the mobile OnboardingStepShell layout: back arrow + progress-bar pill
// (hidden when step === 0 i.e. resume upload) + big title/subtitle + body +
// sticky continue button at the bottom.
import { signOut } from "firebase/auth"
import { auth } from "../../firebaseConfig"

const C = {
  bg:         "#FAFAFA",
  card:       "#FFFFFF",
  border:     "#E4E4E7",
  text:       "#18181b",
  textSec:    "#52525b",
  textMuted:  "#71717a",
  textFaint:  "#a1a1aa",
  progressBg: "#E4E4E7",
  progressFg: "#18181b",
  cta:        "#18181b",
  ctaHov:     "#3f3f46",
  ctaDisabled:"#D4D4D8",
  ctaDisabledText: "#A1A1AA",
  ember:      "#F25A20",
}
const FF = '"Plus Jakarta Sans", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif'

export function OnboardingStepShell({
  title,
  subtitle,
  tag,
  step,
  totalSteps,
  showProgress = true,
  onBack,
  children,
  bottom,
}) {
  const pct = showProgress && totalSteps > 0
    ? Math.min(1, Math.max(0, step / totalSteps))
    : 0

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      width: "100%", maxWidth: 480,
      fontFamily: FF, color: C.text,
    }}>
      {/* Top bar — back + progress pill + sign out */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: C.card, border: `1px solid ${C.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: C.text, flexShrink: 0,
              transition: "background 0.15s, border-color 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#F4F4F5" }}
            onMouseLeave={(e) => { e.currentTarget.style.background = C.card }}
          >
            <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
              <path d="M12 4l-6 6 6 6" stroke={C.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : (
          <div style={{ width: 36, height: 36, flexShrink: 0 }} />
        )}

        {showProgress ? (
          <div style={{
            flex: 1, display: "flex", alignItems: "center", gap: 10,
            padding: "9px 14px",
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: 100,
          }}>
            <div style={{
              flex: 1, height: 5, borderRadius: 100,
              background: C.progressBg, overflow: "hidden",
            }}>
              <div style={{
                width: `${pct * 100}%`, height: "100%",
                background: C.progressFg,
                borderRadius: 100,
                transition: "width 0.3s ease",
              }} />
            </div>
            <span style={{ fontSize: 12, color: C.textMuted, flexShrink: 0, fontWeight: 500 }}>
              {step} / {totalSteps}
            </span>
          </div>
        ) : (
          <div style={{ flex: 1 }} />
        )}

        <button
          type="button"
          onClick={() => signOut(auth)}
          style={{
            background: "none", border: "none", padding: 0,
            fontSize: 12, color: C.textMuted, cursor: "pointer",
            fontFamily: FF, flexShrink: 0,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = C.text }}
          onMouseLeave={(e) => { e.currentTarget.style.color = C.textMuted }}
        >
          Sign out
        </button>
      </div>

      {/* Title block */}
      {tag && (
        <div style={{
          display: "inline-flex", alignSelf: "flex-start",
          padding: "4px 10px",
          background: "#FFF5EC", border: "1px solid #F7D9BE",
          borderRadius: 100,
          fontSize: 11, fontWeight: 700, color: C.ember,
          letterSpacing: "0.04em", marginBottom: 10,
        }}>
          {tag}
        </div>
      )}
      <h1 style={{
        margin: 0, fontSize: 30, lineHeight: 1.1,
        fontWeight: 700, letterSpacing: "-0.03em",
        color: C.text, whiteSpace: "pre-line",
      }}>
        {title}
      </h1>
      {subtitle && (
        <p style={{
          margin: "10px 0 0",
          fontSize: 14.5, lineHeight: 1.5,
          color: C.textSec, fontWeight: 400,
        }}>
          {subtitle}
        </p>
      )}

      {/* Body */}
      <div style={{ marginTop: 24 }}>
        {children}
      </div>

      {/* Bottom sticky action */}
      {bottom && (
        <div style={{ marginTop: 24 }}>
          {bottom}
        </div>
      )}
    </div>
  )
}

export function ContinueButton({ label = "Continue", onClick, loading, disabled, type = "button" }) {
  const dead = loading || disabled
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={dead}
      style={{
        width: "100%", height: 50,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        background: dead ? C.ctaDisabled : C.cta,
        color: dead ? C.ctaDisabledText : "#fff",
        border: "none", borderRadius: 26,
        fontSize: 15, fontWeight: 600, fontFamily: FF,
        cursor: dead ? "not-allowed" : "pointer",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => { if (!dead) e.currentTarget.style.background = C.ctaHov }}
      onMouseLeave={(e) => { if (!dead) e.currentTarget.style.background = C.cta }}
    >
      {loading ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: "obs-spin 0.8s linear infinite" }}>
          <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" />
          <path d="M12 2a10 10 0 0110 10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      ) : label}
    </button>
  )
}

export function RadioOption({ label, leading, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex", alignItems: "center", gap: 14,
        padding: "14px 16px",
        background: C.card,
        border: `1px solid ${selected ? C.text : C.border}`,
        borderRadius: 14,
        cursor: "pointer", fontFamily: FF,
        textAlign: "left",
        transition: "border-color 0.15s, background 0.15s",
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.borderColor = C.textMuted }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.borderColor = C.border }}
    >
      {leading && <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{leading}</div>}
      <span style={{ flex: 1, fontSize: 15, color: C.text, fontWeight: 400 }}>{label}</span>
      <span style={{
        width: 20, height: 20, borderRadius: "50%",
        background: selected ? C.text : "transparent",
        border: `1.5px solid ${selected ? C.text : "#CCCCCC"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.15s, border-color 0.15s",
      }}>
        {selected && (
          <svg width="11" height="11" viewBox="0 0 20 20" fill="none">
            <path d="M4 10l5 5 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
    </button>
  )
}

// Mobile-style card field with inner label on top and input beneath — used for
// text fields in location / contact details / voluntary disclosures.
export function CardField({ label, children, prefix, suffix }) {
  return (
    <div style={{
      display: "flex", alignItems: "center",
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 14, padding: "10px 16px",
      gap: 10,
    }}>
      {prefix && <div style={{ flexShrink: 0, display: "flex", alignItems: "center", color: C.textMuted }}>{prefix}</div>}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {label && (
          <span style={{
            fontSize: 11, fontWeight: 600, color: C.textMuted,
            letterSpacing: "0.03em", marginBottom: 2,
          }}>
            {label}
          </span>
        )}
        {children}
      </div>
      {suffix && <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>{suffix}</div>}
    </div>
  )
}

// Mobile-style dropdown (click to toggle; options render below).
export function InlineDropdown({ id, label, value, hint, options, openId, setOpenId, onSelect }) {
  const open = openId === id
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpenId(open ? null : id)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          padding: "10px 16px",
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 14, cursor: "pointer", fontFamily: FF, textAlign: "left",
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <span style={{
            fontSize: 11, fontWeight: 600, color: C.textMuted,
            letterSpacing: "0.03em", marginBottom: 2,
          }}>
            {label}
          </span>
          <span style={{
            fontSize: 14,
            color: value ? C.text : C.textMuted,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {value || hint}
          </span>
        </div>
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>
          <path d="M5 7l5 5 5-5" stroke={C.textMuted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 14, zIndex: 20,
          maxHeight: 260, overflow: "auto",
          boxShadow: "0 10px 30px rgba(24,24,27,0.08)",
        }}>
          {options.map((opt, i) => {
            const sel = value === opt
            return (
              <button
                key={opt}
                type="button"
                onClick={() => { onSelect(opt); setOpenId(null) }}
                style={{
                  width: "100%", padding: "12px 16px",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: "transparent", border: "none",
                  borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
                  cursor: "pointer", fontFamily: FF,
                  fontSize: 14,
                  color: sel ? C.text : C.text,
                  fontWeight: sel ? 600 : 400,
                  textAlign: "left",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#FAFAFA" }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
              >
                {opt}
                {sel && (
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                    <path d="M4 10l5 5 7-7" stroke={C.text} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export const OB_COLORS = C
export const OB_FF = FF

export const BARE_INPUT_STYLE = {
  border: "none", background: "transparent",
  fontSize: 14, fontWeight: 500, color: C.text,
  outline: "none", fontFamily: FF,
  width: "100%", padding: 0,
}

export const SPIN_KEYFRAMES_CSS = `
  @keyframes obs-spin { to { transform: rotate(360deg); } }
`
